// project import
import Routes from 'routes';
import ThemeCustomization from 'themes';
import RTLLayout from 'components/RTLLayout';
import ScrollTop from 'components/ScrollTop';
import Snackbar from 'components/@extended/Snackbar';
// auth provider
import { JWTProvider as AuthProvider } from 'contexts/JWTContext';
import { StreamChatProvider } from 'contexts/StreamChatContext';
import React, { FC, useEffect, useState } from 'react';
import { ApolloClient, ApolloProvider, NormalizedCacheObject } from '@apollo/client';
import getApolloClient from './apolloClient';
import Loader from './components/Loader';
import { Messages } from './components/@extended/Messages';
import ErrorBoundary from './components/ErrorBoundary';

// ==============================|| APP - AG-GRID  ||============================== //

import {
  ModuleRegistry,
  PaginationModule,
  ValidationModule,
  ColumnAutoSizeModule,
  CellStyleModule,
  TooltipModule,
  CustomFilterModule,
  DateFilterModule,
  NumberFilterModule,
  TextFilterModule,
  EventApiModule,
  ColumnApiModule,
  PinnedRowModule,
  RowStyleModule,
  ClientSideRowModelModule,
  QuickFilterModule,
  RowApiModule,
  ClientSideRowModelApiModule,
  RenderApiModule,
  CsvExportModule,
  RowAutoHeightModule,
  CustomEditorModule
} from 'ag-grid-community';
import {
  LicenseManager,
  provideGlobalGridOptions,
  ServerSideRowModelModule,
  StatusBarModule,
  SideBarModule,
  GroupFilterModule,
  MultiFilterModule,
  SetFilterModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  CellSelectionModule,
  MasterDetailModule,
  ColumnMenuModule,
  ServerSideRowModelApiModule,
  ClipboardModule,
  ExcelExportModule,
  CellSpanModule,
  RichSelectModule
} from 'ag-grid-enterprise';
import { datadogRum } from '@datadog/browser-rum';
import { useNavigate } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import GlobalStyles from './GlobalStyles';
import { McaListFilterProvider } from './pages/merchants/mca-list';
import { LedgerBalanceFilterProvider } from './sections/ledger/InvestorBalanceTransactions';
import { AuditListFilterProvider } from './pages/audit/audit-list';
import { TransactionsFilterProvider } from './pages/transactions/transactions-list';
import { Logger } from './utils/logger';

// Add this at the top of the file for TypeScript
declare global {
  interface Window {
    __APOLLO_CLIENT__?: { clearStore?: () => Promise<void> };
  }
}

if (
  (import.meta.env.VITE_APP_NODE_ENV || 'local').toUpperCase() === 'PROD' ||
  (import.meta.env.VITE_APP_NODE_ENV || 'local').toUpperCase() === 'STAGE'
) {
  datadogRum.init({
    applicationId: import.meta.env.VITE_APP_DDRUM_APP_ID || '69393119-ef6a-4664-a039-ae1268ae80ba',
    clientToken: import.meta.env.VITE_APP_DDRUM_CLIENT_TOKEN || 'puba38dddcabf5ee10e5f7d5a67bec1938e',
    site: 'datadoghq.com',
    service: 'wgf-ui',
    env: import.meta.env.VITE_APP_NODE_ENV,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input'
  });
  datadogRum.startSessionReplayRecording();
}

if (typeof window !== 'undefined' && !window.process) {
  window.process = { env: {} };
}

if (process.env.VITE_APP_NODE_ENV === 'development') {
  ModuleRegistry.registerModules([ValidationModule]);
}
LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-069288}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Rainbow_Advance_LLC}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{GRID_FUNDING}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{GRID_FUNDING}_need_to_be_licensed___{GRID_FUNDING}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{21_December_2025}____[v3]_[01]_MTc2NjI3NTIwMDAwMA==3bce4d2c4df5fb8e67cdff3c2ae441c2'
);
ModuleRegistry.registerModules([
  PaginationModule,
  ServerSideRowModelModule,
  ColumnAutoSizeModule,
  CellStyleModule,
  TooltipModule,
  CustomFilterModule,
  DateFilterModule,
  NumberFilterModule,
  TextFilterModule,
  StatusBarModule,
  SideBarModule,
  GroupFilterModule,
  MultiFilterModule,
  SetFilterModule,
  EventApiModule,
  ColumnApiModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  PinnedRowModule,
  RowStyleModule,
  CellSelectionModule,
  MasterDetailModule,
  ClientSideRowModelModule,
  QuickFilterModule,
  RowApiModule,
  ColumnMenuModule,
  ClientSideRowModelApiModule,
  RenderApiModule,
  ServerSideRowModelApiModule,
  ClipboardModule,
  CsvExportModule,
  ExcelExportModule,
  CellSpanModule,
  RowAutoHeightModule,
  RichSelectModule,
  CustomEditorModule
]);
provideGlobalGridOptions({ theme: 'legacy' });

// ==============================|| APP - THEME, ROUTER, LOCAL  ||============================== //

const logger = new Logger('App');

const App: FC = () => {
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState<ApolloClient<NormalizedCacheObject>>();
  const navigate = useNavigate();
  useEffect(() => {
    async function init() {
      await getApolloClient().then((client) => {
        setClient(client);
        setLoading(false);
      });
    }

    init().catch(console.error);
  }, []);

  useEffect(() => {
    window.onerror = (message, file, lineNo, columnNo, error) => {
      if (error) {
        handleError(error);
        if (error.name === 'QuotaExceededError') {
          // Attempt to clear Apollo cache if available
          if (window.__APOLLO_CLIENT__) {
            window.__APOLLO_CLIENT__.clearStore && window.__APOLLO_CLIENT__.clearStore();
          }
        }
        return true;
      }
      // some browsers like Edge don't supply the last error argument
      const errorMessage = JSON.stringify({
        message,
        file,
        lineNo,
        columnNo
      });
      handleError(new Error(errorMessage));
      return true;
    };
    // handle async errors
    window.onunhandledrejection = (event) => {
      if (event.reason?.name === 'QuotaExceededError') {
        if (window.__APOLLO_CLIENT__) {
          window.__APOLLO_CLIENT__.clearStore && window.__APOLLO_CLIENT__.clearStore();
        }
        console.warn(`UNHANDLED PROMISE REJECTION: LocalStorage quota exceeded: ${event}`);
        return true;
      }
      console.warn(`UNHANDLED PROMISE REJECTION: ${event}`);
      return false;
    };
  }, []);

  const handleError = (error: Error) => {
    logger.error(`window.onerror`, error);
    console.error(error);
    /*if (import.meta.env.VITE_APP_NODE_ENV !== 'development') {
      navigate('/error/');
    }*/
  };

  if (loading || !client) {
    return <Loader />;
  }

  return (
    <ThemeCustomization>
      <RTLLayout>
        <ScrollTop>
          <AuthProvider>
            <StreamChatProvider>
              <ApolloProvider client={client}>
                <CssBaseline />
                <GlobalStyles />
                <McaListFilterProvider>
                <AuditListFilterProvider>
                  <LedgerBalanceFilterProvider>
                    <TransactionsFilterProvider>
                      <ErrorBoundary>
                        <Routes />
                      </ErrorBoundary>
                    </TransactionsFilterProvider>
                  </LedgerBalanceFilterProvider>
                </AuditListFilterProvider>
              </McaListFilterProvider>
                <Snackbar />
                <Messages />
              </ApolloProvider>
            </StreamChatProvider>
          </AuthProvider>
        </ScrollTop>
      </RTLLayout>
    </ThemeCustomization>
  );
};

export default App;
